import React, { FC, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { AdopterDto, AdopterDtoProfileHasBeenAccepted } from '../../interactors/gen/backendClient'

import { setOpacity } from '../../utils/setOpacity'
import { getColorForAdopterProfileHasBeenAccepted } from '../../domain/Adopters/AccountDisplay'

import { AdoptionAttemptChip, AdoptionAttemptWithAnimal } from './AdoptersListScreen'

import { Avatar, Box, Card, CardContent, Collapse, IconButton, Typography } from '@mui/material'

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'

type CardProps = {
  member: AdopterDto
  index: number
  expandCardIndex: number | null
  setExpandCardIndex: React.Dispatch<React.SetStateAction<null | number>>
}

export const AdopterListCard: FC<CardProps> = ({ member, index, expandCardIndex, setExpandCardIndex }) => {
  const attempts = member.adoptionAttempts
  const extraAttemptsCount = Math.max((attempts?.length || 0) - 2, 0)

  const navigate = useNavigate()
  const handleClick = () => {
    navigate(`${member.id}`)
  }
  const [expanded, setExpanded] = React.useState<boolean>(false)
  const handleExpandClick = () => {
    if (!attempts || attempts.length <= 2) {
      handleClick()
      return
    }
    setExpandCardIndex(index)
    setExpanded(!expanded)
  }

  useEffect(() => {
    setExpanded(index === expandCardIndex)
  }, [expandCardIndex])

  // AdoptionAttemptsChip props

  const animalAvatarSx = {
    height: '30px !important',
    width: '30px !important',
    ml: '-1px !important',
    mt: '-1px !important',
  }
  const adoptionChipTextSx = {
    fontSize: 12,
    height: 30,
    pl: 0,
    gap: 0,
  }

  return (
    <Card
      variant="outlined"
      onClick={handleClick}
      sx={{
        borderRadius: '25px',
        border: '1px solid rgba(206, 216, 232, 0.95)',
        boxShadow: '0 1px 2px rgba(32, 56, 100, 0.04)',
      }}
    >
      <CardContent
        sx={{
          paddingX: '20px',
          paddingTop: '20px',
          paddingBottom: '20px',
          '&:last-child': { paddingBottom: '20px' },
        }}
      >
        {/* Avatar, first name + last name + mail */}
        <Box display="flex" gap="10px" alignItems="center" marginBottom="10px">
          {/* First name + last name + email */}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 0.7,
                minWidth: 0,
                flexWrap: 'nowrap',
              }}
            >
              {member.profileHasBeenAccepted && (
                <Avatar
                  alt={"Statut de l'adoptant"}
                  sx={{
                    height: '18px',
                    width: '18px',
                    backgroundColor: setOpacity(
                      getColorForAdopterProfileHasBeenAccepted(member.profileHasBeenAccepted),
                      0.9
                    ),
                    color: '#fff',
                    flexShrink: 0,
                  }}
                >
                  <>{getIconForAdopterProfileHasBeenAccepted(member.profileHasBeenAccepted)}</>
                </Avatar>
              )}
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '16px',
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flexShrink: 1,
                }}
              >
                {member.firstName} {member.lastName}
              </Typography>
              <Typography sx={{ color: '#A1A8BA', fontSize: '13px', flexShrink: 0 }}>|</Typography>
              <Typography
                sx={{
                  fontSize: '13.5px',
                  color: '#6D7690',
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flexShrink: 1,
                }}
              >
                {member.email}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={(e) => {
              e.stopPropagation()
              handleExpandClick()
            }}
            aria-label={expanded ? 'Réduire les procédures' : 'Voir les procédures'}
            sx={{
              width: 30,
              height: 30,
              border: '1px solid rgba(32, 56, 100, 0.12)',
              color: '#203864',
              backgroundColor: 'rgba(255,255,255,0.94)',
              flexShrink: 0,
              alignSelf: 'center',
              p: 0,
            }}
          >
            {expanded ? (
              <ExpandMoreRoundedIcon sx={{ fontSize: 20 }} />
            ) : (
              <ChevronRightRoundedIcon sx={{ fontSize: 20 }} />
            )}
          </IconButton>
        </Box>

        {/* If adoption(s): 1 or 2 first chips */}
        {Array.isArray(attempts) && attempts.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
              minWidth: 0,
              alignItems: 'center',
              marginBottom: extraAttemptsCount > 0 ? '6px' : 0,
            }}
          >
            {attempts.slice(0, 2).map((attempt) => (
              <AdoptionAttemptChip
                key={attempt.id}
                member={member}
                attempt={attempt as AdoptionAttemptWithAnimal}
                avatarSx={animalAvatarSx}
                textSx={adoptionChipTextSx}
              />
            ))}
            {extraAttemptsCount > 0 && (
              <Box
                component="span"
                sx={{
                  minWidth: 34,
                  height: 30,
                  px: 1,
                  borderRadius: '999px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#fff',
                  border: '1px solid rgba(105, 141, 172, 0.3)',
                  color: '#203864',
                  fontWeight: 700,
                  fontSize: 11,
                  lineHeight: 1,
                  flexShrink: 0,
                }}
              >
                +{extraAttemptsCount}
              </Box>
            )}
          </Box>
        )}

        {/* More than 2 adoptions: collapse + expand button */}
        {Array.isArray(attempts) && attempts.length > 2 && (
          <Box paddingBottom="0">
            <Collapse in={expanded}>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 0.5,
                  mt: 0.15,
                }}
              >
                {attempts.slice(2).map((attempt) => (
                  <AdoptionAttemptChip
                    key={attempt.id}
                    member={member}
                    attempt={attempt as AdoptionAttemptWithAnimal}
                    avatarSx={animalAvatarSx}
                    textSx={adoptionChipTextSx}
                  />
                ))}
              </Box>
            </Collapse>
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

const getIconForAdopterProfileHasBeenAccepted = (
  profileHasBeenAccepted: AdopterDtoProfileHasBeenAccepted
): JSX.Element | undefined => {
  const iconSx = {
    fontSize: '11px',
    color: 'white',
  }
  switch (profileHasBeenAccepted) {
    case AdopterDtoProfileHasBeenAccepted.Accepted:
      return <CheckCircleOutlineIcon sx={iconSx} />
    case AdopterDtoProfileHasBeenAccepted.FormAccepted:
      return <HourglassEmptyIcon sx={iconSx} />
    case AdopterDtoProfileHasBeenAccepted.Refused:
      return <CancelOutlinedIcon sx={iconSx} />
    default:
      return undefined
  }
}
